import React, { useState } from 'react';
import { useTemplates, SmartContractTemplate, TokenStandard, AuditStatus } from '../../hooks/useSmartContractTemplates';
import { AuditStatusBadge } from './AuditStatusBadge';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { FileCode, Check, Search, Filter } from 'lucide-react';

interface TemplateSelectorProps {
  tokenStandard?: TokenStandard;
  onSelect: (template: SmartContractTemplate) => void;
  selectedTemplateId?: string;
  showAuditStatus?: boolean;
  filterAudited?: boolean;
}

const tokenStandardLabels: Record<TokenStandard, string> = {
  ERC20: 'ERC-20 (Fungível)',
  ERC1400: 'ERC-1400 (Security Token)',
  ERC4626: 'ERC-4626 (Vault)',
};

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  tokenStandard,
  onSelect,
  selectedTemplateId,
  showAuditStatus = true,
  filterAudited = false,
}) => {
  const [search, setSearch] = useState('');
  const [selectedStandard, setSelectedStandard] = useState<TokenStandard | undefined>(tokenStandard);

  const { data, isLoading, error } = useTemplates({
    tokenStandard: selectedStandard,
    auditStatus: filterAudited ? 'AUDITED' : undefined,
    isActive: true,
    search: search || undefined,
    limit: 50,
  });

  const templates = data?.data || [];

  if (isLoading) {
    return (
      <div className="bg-surface-800 rounded-lg p-6 border border-surface-700">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-surface-800 rounded-lg p-6 border border-surface-700">
        <div className="text-center py-8 text-red-400">
          Erro ao carregar templates
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-800 rounded-lg border border-surface-700">
      {/* Header with filters */}
      <div className="p-4 border-b border-surface-700 space-y-3">
        <div className="flex items-center gap-2">
          <FileCode className="w-5 h-5 text-primary-400" />
          <h3 className="text-lg font-medium text-surface-100">Selecionar Template</h3>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
          <input
            type="text"
            placeholder="Buscar template..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Token Standard Filter */}
        {!tokenStandard && (
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-surface-400" />
            <button
              onClick={() => setSelectedStandard(undefined)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                !selectedStandard
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                  : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
              }`}
            >
              Todos
            </button>
            {(['ERC20', 'ERC1400', 'ERC4626'] as TokenStandard[]).map((standard) => (
              <button
                key={standard}
                onClick={() => setSelectedStandard(standard)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedStandard === standard
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                    : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
                }`}
              >
                {standard}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Template List */}
      <div className="max-h-96 overflow-y-auto">
        {templates.length === 0 ? (
          <div className="p-8 text-center text-surface-400">
            Nenhum template encontrado
          </div>
        ) : (
          <div className="divide-y divide-surface-700">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => onSelect(template)}
                className={`p-4 cursor-pointer transition-colors ${
                  selectedTemplateId === template.id
                    ? 'bg-primary-500/10 border-l-2 border-primary-500'
                    : 'hover:bg-surface-700/50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-surface-100 truncate">
                        {template.name}
                      </h4>
                      {selectedTemplateId === template.id && (
                        <Check className="w-4 h-4 text-primary-400 flex-shrink-0" />
                      )}
                    </div>
                    
                    {template.description && (
                      <p className="text-sm text-surface-400 mt-1 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="info">
                        {tokenStandardLabels[template.tokenStandard]}
                      </Badge>
                      <span className="text-xs text-surface-500">
                        v{template.currentVersion}
                      </span>
                      {template._count?.deployments !== undefined && template._count.deployments > 0 && (
                        <span className="text-xs text-surface-500">
                          {template._count.deployments} deploy{template._count.deployments !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {showAuditStatus && (
                    <div className="flex-shrink-0">
                      <AuditStatusBadge
                        status={template.auditStatus}
                        auditorName={template.auditorCompany}
                        auditDate={template.auditDate}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {templates.length > 0 && (
        <div className="p-3 border-t border-surface-700 text-center text-sm text-surface-400">
          {templates.length} template{templates.length !== 1 ? 's' : ''} encontrado{templates.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
